"use client";

import { NPSInline, type NPSInlineStep } from "@/components/ui/nps-inline";
import { useNPSTimer } from "@/hooks/useNPSTimer";
import {
  type AudienceSurveyPosition,
  captureAudienceSurveyAccepted,
  captureAudienceSurveyCompleted,
  captureAudienceSurveyDismissed,
  captureAudienceSurveySaveFailed,
  captureAudienceSurveyShown,
  captureAudienceSurveyStepCompleted,
  captureAudienceSurveySubmitted,
} from "@/lib/analytics/nps-events";
import { Z_INDEX_CLASSES } from "@/lib/constants/z-index";
import { getNpsLastGame } from "@/lib/nps/session";
import {
  type AudienceSurveyRef,
  saveAudienceSurveyProgress,
  startAudienceSurveyResponse,
  submitNpsResponse,
} from "@/lib/nps/submit";
import { XIcon } from "@phosphor-icons/react";
import { usePathname } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import { useCallback, useEffect, useRef, useState } from "react";

function isGameTheaterPath(pathname: string | null): boolean {
  if (!pathname) return false;
  // Direct game page or intercepting overlay: /games/[slug]
  return /^\/games\/[^/]+\/?$/.test(pathname);
}

/**
 * Site-wide sticky survey after the existing dwell timer, shown only when the
 * visitor is not inside the game theater — so the form never covers gameplay.
 */
export function StickyNPSSurvey() {
  const pathname = usePathname();
  const posthog = usePostHog();
  const { shouldShowNPS, persistNpsCompleted, dismissNPS } = useNPSTimer();
  const [preview, setPreview] = useState(false);
  const [testMode, setTestMode] = useState(false);
  const [previewDismissed, setPreviewDismissed] = useState(false);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    // Local design review uses the real site shell without sending responses.
    if (
      process.env.NODE_ENV === "development" ||
      process.env.NEXT_PUBLIC_SURVEY_DESIGN_PREVIEW === "true"
    ) {
      setPreview(params.get("surveyPreview") === "1");
    }
    // Local-only end-to-end trigger: bypass eligibility but use the real API/database path.
    if (process.env.NODE_ENV !== "production") {
      setTestMode(params.get("surveyTest") === "1");
    }
  }, []);
  const lastGame =
    getNpsLastGame() ??
    (preview || testMode
      ? { slug: testMode ? "survey-pipeline-test" : "survey-preview", id: null }
      : null);
  const [step, setStep] = useState<NPSInlineStep>("invite");
  const [position, setPosition] = useState<AudienceSurveyPosition>("invite");
  const shownForSlugRef = useRef<string | null>(null);
  const surveyRef = useRef<AudienceSurveyRef | null>(null);
  const surveyStart = useRef<Promise<AudienceSurveyRef> | null>(null);
  const surveyOpen = useRef(false);
  const surveyEpoch = useRef(0);
  const progressQueue = useRef<Promise<void>>(Promise.resolve());

  const abandonSurveyAttempt = () => {
    surveyOpen.current = false;
    surveyEpoch.current += 1;
    surveyRef.current = null;
    surveyStart.current = null;
  };

  const [anotherDialogOpen, setAnotherDialogOpen] = useState(false);
  useEffect(() => {
    const check = () =>
      setAnotherDialogOpen(
        Boolean(
          document.querySelector(
            '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"], dialog[open]'
          )
        )
      );
    check();
    const observer = new MutationObserver(check);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["data-state", "open"],
    });
    return () => observer.disconnect();
  }, []);

  const inTheater = isGameTheaterPath(pathname);
  const forcedVisible = preview || testMode;
  const visible =
    (forcedVisible ? !previewDismissed : shouldShowNPS) &&
    !inTheater &&
    !anotherDialogOpen &&
    Boolean(lastGame?.slug);

  useEffect(() => {
    if (preview || testMode) return;
    if (!visible || !lastGame?.slug) {
      if (!shouldShowNPS) {
        shownForSlugRef.current = null;
        abandonSurveyAttempt();
        setStep("invite");
      }
      return;
    }
    if (shownForSlugRef.current === lastGame.slug) return;
    shownForSlugRef.current = lastGame.slug;
    captureAudienceSurveyShown(posthog?.capture.bind(posthog), lastGame.slug);
  }, [visible, shouldShowNPS, lastGame?.slug, posthog, preview, testMode]);

  const handleStepChange = useCallback((next: NPSInlineStep) => {
    setStep(next);
  }, []);

  const handleDismissClick = () => {
    if (preview || testMode) {
      setPreviewDismissed(true);
      if (testMode) {
        abandonSurveyAttempt();
        dismissNPS();
      }
      return;
    }
    if (!lastGame?.slug) {
      abandonSurveyAttempt();
      dismissNPS();
      return;
    }
    captureAudienceSurveyDismissed(posthog?.capture.bind(posthog), lastGame.slug, position);
    abandonSurveyAttempt();
    dismissNPS();
  };

  const handleComplete = () => {
    if (preview || testMode) {
      setPreviewDismissed(true);
      if (testMode) {
        abandonSurveyAttempt();
        dismissNPS();
      }
      return;
    }
    abandonSurveyAttempt();
    dismissNPS();
  };

  const ensureSurveyStarted = (): Promise<AudienceSurveyRef> => {
    if (surveyRef.current) {
      return Promise.resolve(surveyRef.current);
    }
    if (surveyStart.current) {
      return surveyStart.current;
    }
    if (!surveyOpen.current) {
      return Promise.reject(new Error("Survey is closed."));
    }
    if (!lastGame?.slug) {
      return Promise.reject(new Error("No game is available for this survey."));
    }

    const attempt = startAudienceSurveyResponse({
      pagePath: pathname || "/",
      gameSlug: lastGame.slug,
      gameId: lastGame.id,
    }).then((started) => {
      if (surveyStart.current === attempt) {
        surveyRef.current = started;
      }
      return started;
    });
    surveyStart.current = attempt;
    void attempt.catch((error) => {
      if (surveyStart.current === attempt) {
        surveyStart.current = null;
      }
      if (lastGame?.slug && !testMode) {
        captureAudienceSurveySaveFailed(posthog?.capture.bind(posthog), lastGame.slug, "start");
      }
      console.error("Audience survey start failed", error);
    });
    return attempt;
  };

  if (!visible || !lastGame?.slug) {
    return null;
  }

  const dismissButtonClassName =
    "inline-flex h-7 w-7 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors";
  // Invite already has "No thanks!" — a reserved X row would leave a blank
  // band and shove the copy into the bottom-left on a narrow screen.
  const showChromeDismiss = step !== "invite" && step !== "thankyou";

  return (
    <aside
      data-testid="nps-survey"
      className={`fixed inset-x-0 bottom-0 ${Z_INDEX_CLASSES.GAME_NPS_OVERLAY} border-t border-gray-200 bg-white shadow-[0_-8px_24px_rgba(0,0,0,0.12)]`}
      aria-label="Feedback survey"
    >
      {showChromeDismiss ? (
        <div className="flex h-9 justify-end px-2 pt-1.5 sm:hidden">
          <button
            type="button"
            onClick={handleDismissClick}
            className={dismissButtonClassName}
            aria-label="Dismiss feedback survey"
            data-testid="nps-dismiss"
          >
            <XIcon weight="bold" size={14} />
          </button>
        </div>
      ) : null}
      {showChromeDismiss ? (
        <button
          type="button"
          onClick={handleDismissClick}
          className={`absolute left-2 top-2 hidden sm:inline-flex ${dismissButtonClassName}`}
          aria-label="Dismiss feedback survey"
          data-testid="nps-dismiss-desktop"
        >
          <XIcon weight="bold" size={14} />
        </button>
      ) : null}
      <div
        className={`mx-auto max-h-[min(70dvh,28rem)] w-full max-w-3xl overflow-y-auto px-3 pb-[max(0.5rem,env(safe-area-inset-bottom))] pt-2 sm:py-2 ${
          showChromeDismiss ? "sm:pl-12 sm:pr-4" : "sm:px-4"
        }`}
      >
        <NPSInline
          onStart={() => {
            if (preview) {
              return;
            }
            if (!testMode) {
              captureAudienceSurveyAccepted(posthog?.capture.bind(posthog), lastGame.slug);
            }
            surveyEpoch.current += 1;
            surveyOpen.current = true;
            progressQueue.current = Promise.resolve();
            surveyRef.current = null;
            surveyStart.current = null;
            void ensureSurveyStarted();
          }}
          onSaveProgress={(details) => {
            if (preview) return;
            const epoch = surveyEpoch.current;
            const boundRef = surveyRef.current;
            const boundStart = surveyStart.current;
            progressQueue.current = progressQueue.current
              .catch(() => undefined)
              .then(async () => {
                const bound = boundRef ?? (boundStart ? await boundStart.catch(() => null) : null);
                const current =
                  bound ??
                  (surveyOpen.current && surveyEpoch.current === epoch
                    ? await ensureSurveyStarted()
                    : null);
                if (!current) return;
                await saveAudienceSurveyProgress(current, details);
              })
              .catch((error) => {
                if (!testMode) {
                  captureAudienceSurveySaveFailed(
                    posthog?.capture.bind(posthog),
                    lastGame.slug,
                    "progress"
                  );
                }
                console.error("Audience survey progress save failed", error);
              });
          }}
          onSubmitScore={async (details) => {
            if (preview) {
              return { id: "design-preview" };
            }
            try {
              await progressQueue.current;
              const current = await ensureSurveyStarted();
              const result = await submitNpsResponse(current, details);
              persistNpsCompleted();
              if (!testMode) {
                captureAudienceSurveySubmitted(
                  posthog?.capture.bind(posthog),
                  lastGame.slug,
                  details
                );
                captureAudienceSurveyCompleted(
                  posthog?.capture.bind(posthog),
                  lastGame.slug,
                  details
                );
              }
              return result;
            } catch (error) {
              if (!testMode) {
                captureAudienceSurveySaveFailed(
                  posthog?.capture.bind(posthog),
                  lastGame.slug,
                  "submit"
                );
              }
              throw error;
            }
          }}
          onDismiss={handleDismissClick}
          onComplete={handleComplete}
          onStepChange={handleStepChange}
          onPositionChange={setPosition}
          onStepCompleted={(details) => {
            if (preview || testMode) return;
            captureAudienceSurveyStepCompleted(
              posthog?.capture.bind(posthog),
              lastGame.slug,
              details.step === "feedback"
                ? {
                    step: "feedback",
                    answers: details.answers,
                    has_feedback: details.hasFeedback,
                  }
                : details
            );
          }}
        />
      </div>
    </aside>
  );
}
